'use strict';

// var log = require('../../../../log/Log').channel('AudioDspDevice');

// This mixin makes implementing audio dsp drivers much easier. In addition to
// applying the prototype to your driver's prototype, you will need to
// implement the following methods to be used as arguments to the methods in
// this mixin.
//
// configToChannelNames(ctrl, cfg) => array of strings
//
// The purpose of this method is to return an array of strings corresponding
// to channel names for your driver given a configuration point. This is used
// by the mixin to determine if there are duplicate channels during validation.
//
// For example, if the mixing was evaluating one of the sound reinforcment zone
// mute configurations for a Biamp Tesira, the input arguments would look like:
//
// ctrl = {
//     cmdName: 'soundReinforcementMute',
//     cfgName: 'soundReinforcement',
//     subCfgName: 'mute',
//     idName: 'zoneId',
//     filter: 'soundReinforcementMute',
//     type: 'mute',
//     allowDuplicate: 'soundReinforcementVolume',
//     paths: ...the paths object for this control (see below)
// };
//
// cfg = {
//     mutePreset: 'mute preset'
//     unmutePreset: 'unmute preset',
//     muteStatus: {
//         instanceTag: 'foo',
//         index: 2,
//      },
// };
//
// And the method would return something like:
//   [ 'mute preset', 'unmute preset', 'foo.2' ]
//
// Simple configurations may only return an array with a single element, but
// the array format is required for more complicated cases like mutes via
// presets and telephony configurations requiring multiple control points or
// an optional receive gain.
//
// validateConfig(ctrl, cfg)
//
// The purpose of this method is to return 'noChannel', 'invalidChannel', or
// 'ok' as approprate for validating the given configuration point for the
// given control. The arguments are the same as for the configToChannelNames
// method above.
//
// duplicateAllowed(ctrl, path, name, numDuplicates)
//
// This method is called during validation when a duplicate channel name is
// detected. This method should return true if the duplicate is allowed, false
// if not.
//
// The ctrl argument is the control for which the channel is being evaluated.
//
// The path argument is the current path element from the control which is
// being evaluated (see the computeAudioControls method for info on paths).
//
// The name argument is the channel name which was found to be duplicated.
// Remember, a given configuration point can result in multiple channel names.
//
// The numDuplicates argument indicates how many times this name appears in the
// entire configuration.
//
// send(ctrl, cfg, devValue)
//
// This method is called to send a command. Your implementation should use this
// to send the appropriate command (mute, gain, etc.) to the device. The
// arguments are the control (as described above), the configuration point (as
// described above), and the value to send. The value will be boolean for mute
// controls and will be a number for volume controls. In the case of volume
// controls, the number has already been converted to the appropriate range
// for your device and channel.
//
// getRange(ctrl, cfg)
//
// This method should return a range object with 'min' and 'max' properties
// indicating the minimum and maximum device values for the specified
// configuration point used for the specified control. These are used to
// convert the system volume range to your device's volume range. If your
// device also needs the value to be rounded, you can specify the optional
// boolean 'round' property in the returned object as well.
//
// match(ctrl, cfg)
//
// This function should return true if the specified configuration point
// matches the specified control. This is used by doAudioUpdate when updates
// are received from your device. This method is called to determine if the
// update is for the specifed control and configuration point.

var audioControlsDef = [{
    cmdName: 'volume',
    cfgName: 'amplifier',
    filter: 'amplifier',
    type: 'volume',
    subType: 'amplifier',
    min: 0,
    max: 50,
}, {
    cmdName: 'volumeMute',
    cfgName: 'amplifier',
    // no filter -- it doesn't get validated
    type: 'mute',
}, {
    cmdName: 'assistiveListeningMute',
    cfgName: 'assistiveListening',
    subCfgName: 'mute',
    filter: 'assistiveListeningMute',
    type: 'mute',
    allowDuplicate: 'assistiveListeningVolume',
}, {
    cmdName: 'assistiveListeningVolume',
    cfgName: 'assistiveListening',
    subCfgName: 'volume',
    filter: 'assistiveListeningVolume',
    type: 'volume',
    subType: 'trim',
    min: -12,
    max: 12,
    allowDuplicate: 'assistiveListeningMute',
}, {
    cmdName: 'audioInputMute',
    cfgName: 'audioInputSources',
    subCfgName: 'mute',
    idName: 'srcId',
    filter: 'audioInputMute',
    type: 'mute',
    allowDuplicate: 'audioInputVolume',
}, {
    cmdName: 'audioInputVolume',
    cfgName: 'audioInputSources',
    subCfgName: 'volume',
    idName: 'srcId',
    filter: 'audioInputVolume',
    type: 'volume',
    subType: 'trim',
    min: -12,
    max: 12,
    allowDuplicate: 'audioInputMute',
}, {
    cmdName: 'mute',
    cfgName: 'mics',
    filter: 'mics',
    type: 'mute',
}, {
    cmdName: 'overflowRoomMute',
    cfgName: 'overflowRooms',
    subCfgName: 'mute',
    idName: 'roomId',
    filter: 'overflowRoomMute',
    type: 'mute',
    allowDuplicate: 'overflowRoomVolume',
}, {
    cmdName: 'overflowRoomVolume',
    cfgName: 'overflowRooms',
    subCfgName: 'volume',
    idName: 'roomId',
    filter: 'overflowRoomVolume',
    type: 'volume',
    subType: 'trim',
    min: -12,
    max: 12,
    allowDuplicate: 'overflowRoomMute',
}, {
    cmdName: 'soundReinforcementMute',
    cfgName: 'soundReinforcement',
    subCfgName: 'mute',
    idName: 'zoneId',
    filter: 'soundReinforcementMute',
    type: 'mute',
    allowDuplicate: 'soundReinforcementVolume',
}, {
    cmdName: 'soundReinforcementVolume',
    cfgName: 'soundReinforcement',
    subCfgName: 'volume',
    idName: 'zoneId',
    filter: 'soundReinforcementVolume',
    type: 'volume',
    subType: 'trim',
    min: -12,
    max: 12,
    allowDuplicate: 'soundReinforcementMute',
}, {
    // no cmdName
    cfgName: 'telephony',
    idName: 'id',
    filter: 'telephony',
    type: 'telephony',
}];

function AudioDspDevice() {
}

// Return an audioControls array, similar to audioControlsDef, but each element
// is augmented with a "paths" array with each element having the following
// properties:
//     config: array defining the control's path within the config
//     value: array defining the value's path within the driver (only if cmdName defined)
//     id: if applicable, the id of the control in the config
//
// The "paths" property is an array because some controls (e.g., overflow
// rooms) can contain multiple entries. For controls with a single entry
// (e.g., assistive listening), jsut access the first element in the array.
//
// If no definition for the a control exists in the config, then that control
// element will have no "paths" property.

AudioDspDevice.prototype.computeAudioControls = function(config, includeTelephony) {
    if (!config) config = this.config;
    if (typeof includeTelephony === 'undefined') includeTelephony = true;

    var audioControls = JSON.parse(JSON.stringify(audioControlsDef));

    if (!includeTelephony) audioControls.pop(); // assume telephony is last element

    audioControls.forEach(function(ctrl) {
        if (!config.hasOwnProperty(ctrl.cfgName)) return;

        if (ctrl.idName) {
            var paths = [];

            Object.keys(config[ctrl.cfgName]).forEach(function(id) {
                var cfg = config[ctrl.cfgName][id];

                if (ctrl.subCfgName && !cfg.hasOwnProperty(ctrl.subCfgName)) return;

                var path = {
                    config: ctrl.subCfgName ?
                        [ ctrl.cfgName, id, ctrl.subCfgName ] : [ ctrl.cfgName, id ],
                    id: id,
                };

                if (ctrl.hasOwnProperty('cmdName')) path.value = [ ctrl.cmdName, id ];

                paths.push(path);
            }, this);

            if (paths.length > 0) ctrl.paths = paths;
        } else {
            if (ctrl.subCfgName && !config[ctrl.cfgName].hasOwnProperty(ctrl.subCfgName)) return;

            ctrl.paths = [{
                config: ctrl.subCfgName ? [ ctrl.cfgName, ctrl.subCfgName ] : [ ctrl.cfgName ],
            }];

            if (ctrl.hasOwnProperty('cmdName')) ctrl.paths[0].value = [ ctrl.cmdName ];
        }
    }, this);

    return audioControls;
};

// Compute validation results for the audio controls. Takes the following
// arguments:
//     validated: the validation object to poplulate with the results
//     configToChannelNames: see description at the top of this file
//     validateConfig: see description at the top of this file
//     duplicateAllowed: see description at the top of this file
//     config: the device's configuration, if undefined will use this.config
//     audioControls: the audio controls (with computed paths), if undefined
//         will use this.audioControls

AudioDspDevice.prototype.computeAudioControlValidationResults = function(
    validated, configToChannelNames, validateConfig, duplicateAllowed, config, audioControls) {
    if (!config) config = this.config;
    if (!audioControls) audioControls = this.audioControls;

    var channelCounts = {};

    // First pass: count references for valid channels. This will be used in
    // the second pass for determining if there are duplicate channels.

    audioControls.forEach(function(ctrl) {
        if (!ctrl.paths || !ctrl.hasOwnProperty('filter')) return;

        ctrl.paths.forEach(function(path) {
            var channelNames = configToChannelNames(ctrl, this.getNode(config, path.config));
            channelNames.forEach(function(name) {
                if (!channelCounts.hasOwnProperty(name)) channelCounts[name] = {};
                channelCounts[name][ctrl.type] = channelCounts[name][ctrl.type] ?
                    channelCounts[name][ctrl.type] + 1 : 1;
            }, this);
        }, this);
    }, this);

    // Second pass: compute validation results.

    audioControls.forEach(function(ctrl) {
        if (!ctrl.paths || !ctrl.hasOwnProperty('filter')) return;

        ctrl.paths.forEach(function(path) {

            var cfg = this.getNode(config, path.config);
            var valid = validateConfig(ctrl, cfg);

            if (valid === 'ok') {
                var channelNames = configToChannelNames(ctrl, cfg);
                channelNames.forEach(function(name) {
                    if (this.getNode(channelCounts, [ name, ctrl.type ], 0) > 1) {
                        if (!duplicateAllowed ||
                            !duplicateAllowed(ctrl, path, name, channelCounts[name])) {
                            valid = 'duplicateChannel';
                        }
                    }
                }, this);
            }

            this.setNode(validated, path.config, valid);
        }, this);
    }, this);
};

AudioDspDevice.prototype.convertVolume = function(x, xMin, xMax, yMin, yMax, round) {
    var y = (x - xMin) * (yMax - yMin) / (xMax - xMin) + yMin;
    if (round) y = Math.round(y);
    if (y > yMax) y = yMax;
    if (y < yMin) y = yMin;
    return y;
};

// Helper for get functions. Arguments:
//     cmdName: the name of the command
//     audioControls: the audio controls (will use this.audioControls if
//         undefined)

AudioDspDevice.prototype.doAudioGet = function(cmdName, audioControls) {
    if (!audioControls) audioControls = this.audioControls;

    var ctrl = this.getAudioControl(cmdName, audioControls);
    var value;

    if (ctrl.idName) {
        value = {};

        ctrl.paths.forEach(function(path) {
            value = { value: this.getNode(this, path.value) };
            value[ctrl.idName] = path.id;
            this.broadcastStatus(cmdName, value);
        }, this);
    } else {
        value = this.getNode(this, ctrl.paths[0].value);
        this.broadcastStatus(cmdName, value);
    }
};

// Helper for set functions. Arguments:
//     cmdName: the name of the command
//     value: the value to set, as supplied by EquipmentService; it will be a
//         simple value for non-collection commands, and will be an object
//         containing the value and id for collection commands
//     send: see description at the top of this file
//     getRange: see description at the top of this file
//     config: the device's configuration (will use this.config if undefined)
//     audioControls: the device's audio controls (will use this.audioControls
//         if undefined)

AudioDspDevice.prototype.doAudioSet = function(cmdName, value, send, getRange, config, audioControls) {
    if (!config) config = this.config;
    if (!audioControls) audioControls = this.audioControls;

    var ctrl = this.getAudioControl(cmdName);
    var path;
    var devValue;

    if (ctrl.idName) {
        devValue = value.value;
        path = ctrl.paths.find(function(path) {
            return path.id === value[ctrl.idName];
        });
    } else {
        devValue = value;
        path = ctrl.paths[0];
    }

    this.setNode(this, path.value, value);
    this.broadcastStatus(cmdName, value);

    var cfg = this.getNode(config, path.config);

    if (ctrl.type === 'volume') {
        var dev = getRange(ctrl, cfg);
        devValue = this.convertVolume(devValue, ctrl.min, ctrl.max, dev.min, dev.max, dev.round);
    }

    send(ctrl, cfg, devValue);
};

// Helper function for updating an audio control based on a status message from
// the device. Arguments:
//     devValue: the value reported by the device (boolean for mute, number for
//         volume)
//     match: see description at the top of this file
//     getRange: see description at the top of this file
//     config: the device's configuration (will use this.config if undefined)
//     audioControls: the device's audio controls (will use this.audioControls
//         if undefined)

AudioDspDevice.prototype.doAudioUpdate = function(devValue, match, getRange, config, audioControls) {
    if (!config) config = this.config;
    if (!audioControls) audioControls = this.audioControls;

    var ctrl;
    var paths = null;

    for (var i = 0; i < audioControls.length; i++) {
        ctrl = audioControls[i];

        if (!ctrl.hasOwnProperty('cmdName') || !ctrl.hasOwnProperty('paths')) continue;

        for (var j = 0; j < ctrl.paths.length; j++) {
            if (match(ctrl, this.getNode(config, ctrl.paths[j].config))) {
                paths = ctrl.paths[j];
            }
        }

        if (paths !== null) break;
    }

    if (paths === null) return undefined;

    var value = devValue;

    if (ctrl.type === 'volume') {
        var dev = getRange(ctrl, this.getNode(config, paths.config));
        value = this.convertVolume(devValue, dev.min, dev.max, ctrl.min, ctrl.max, true);
    }

    var bValue = value;

    if (ctrl.hasOwnProperty('idName')) {
        bValue = { value: value };
        bValue[ctrl.idName] = paths.id;
    }

    var currentValue = this.getNode(this, paths.value);

    if (currentValue !== value) {
        this.setNode(this, paths.value, value);

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus(ctrl.cmdName, bValue);
        }
    }

    return ctrl;
};

AudioDspDevice.prototype.getAudioControl = function(cmdName, audioControls) {
    if (!audioControls) audioControls = this.audioControls;

    return audioControls.find(function(ctrl) {
        return ctrl.cmdName === cmdName;
    }, this);
};


module.exports = AudioDspDevice;
